import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    BarChart3,
    Check,
    Copy,
    ExternalLink,
    Globe,
    Image as ImageLucide,
    Loader2,
    RefreshCw,
    Sparkles,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

async function jsonReq(url, opts = {}) {
    const res = await fetch(url, {
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
        ...opts,
    });
    if (!res.ok) {
        let d;
        try {
            d = (await res.json()).detail;
        } catch {
            d = res.statusText;
        }
        throw new Error(d || `Request failed: ${res.status}`);
    }
    return res.status === 204 ? null : res.json();
}

function CopyBadge({ value, testId }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            data-testid={testId}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                } catch {
                    toast.error("Copy failed");
                }
            }}
            className="inline-flex items-center gap-1.5 h-7 px-2 border border-foreground font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-foreground hover:text-background transition-colors"
        >
            {copied ? (
                <>
                    <Check className="w-3 h-3" strokeWidth={1.5} /> Copied
                </>
            ) : (
                <>
                    <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
                </>
            )}
        </button>
    );
}

export default function DeployDialog({
    open,
    onOpenChange,
    project,
    publicUrl,
    onPublish,
    onUnpublish,
    onRefresh,
    publishing,
}) {
    const [domains, setDomains] = useState([]);
    const [cnameTarget, setCnameTarget] = useState("sites.loom.app");
    const [loading, setLoading] = useState(true);
    const [newDomain, setNewDomain] = useState("");
    const [adding, setAdding] = useState(false);
    const [verifying, setVerifying] = useState(null);
    const [ogRegenerating, setOgRegenerating] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const pollTimerRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!project) return;
        setLoading(true);
        try {
            const data = await jsonReq(`${API}/projects/${project.project_id}/domains`);
            setDomains(data.domains || []);
            if (data.cname_target) setCnameTarget(data.cname_target);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    // Poll while OG image is generating so the thumbnail appears without a manual reload.
    useEffect(() => {
        const clearPoll = () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        if (!open || !project?.published) {
            clearPoll();
            return;
        }
        const pending = project?.og_generating || (!project?.og_image_url);
        if (!pending) {
            clearPoll();
            return;
        }
        if (pollTimerRef.current || !onRefresh) return;
        pollTimerRef.current = setInterval(async () => {
            try {
                await onRefresh();
            } catch {
                // Silent — will retry on next tick.
            }
        }, 3000);
        return clearPoll;
    }, [open, project?.published, project?.og_generating, project?.og_image_url, onRefresh]);

    const regenerateOg = async () => {
        if (!project) return;
        setOgRegenerating(true);
        try {
            await jsonReq(`${API}/projects/${project.project_id}/og-image`, {
                method: "POST",
            });
            toast.success("OG image regenerated");
            if (onRefresh) await onRefresh();
        } catch (e) {
            toast.error(e.message || "Regeneration failed");
        } finally {
            setOgRegenerating(false);
        }
    };

    const toggleShowcase = async (nextValue) => {
        if (!project) return;
        try {
            await jsonReq(`${API}/projects/${project.project_id}/showcase`, {
                method: "POST",
                body: JSON.stringify({ showcase_public: nextValue }),
            });
            toast.success(nextValue ? "Added to Loom Showcase" : "Removed from Loom Showcase");
            if (onRefresh) await onRefresh();
        } catch (e) {
            toast.error(e.message || "Could not update Showcase status");
        }
    };

    // Load analytics when the dialog opens on a published project.
    const loadAnalytics = useCallback(async () => {
        if (!project?.project_id || !project?.published) {
            setAnalytics(null);
            return;
        }
        setAnalyticsLoading(true);
        try {
            const data = await jsonReq(`${API}/projects/${project.project_id}/analytics?days=7`);
            setAnalytics(data);
        } catch {
            setAnalytics(null);
        } finally {
            setAnalyticsLoading(false);
        }
    }, [project?.project_id, project?.published]);

    useEffect(() => {
        if (open) loadAnalytics();
    }, [open, loadAnalytics]);

    const analyticsMaxViews = useMemo(() => {
        if (!analytics?.series?.length) return 0;
        return Math.max(...analytics.series.map((d) => d.views));
    }, [analytics]);

    const addDomain = async () => {
        const val = newDomain.trim().toLowerCase();
        if (!val) return;
        setAdding(true);
        try {
            await jsonReq(`${API}/projects/${project.project_id}/domains`, {
                method: "POST",
                body: JSON.stringify({ domain: val }),
            });
            setNewDomain("");
            toast.success(`${val} attached`);
            refresh();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setAdding(false);
        }
    };

    const verify = async (dom) => {
        setVerifying(dom.domain);
        try {
            const res = await jsonReq(
                `${API}/projects/${project.project_id}/domains/${dom.domain}/verify`,
                { method: "POST" },
            );
            if (res.verified) {
                toast.success(`${dom.domain} verified`);
                refresh();
            } else {
                toast.error(res.message || "Not verified yet");
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setVerifying(null);
        }
    };

    const removeDomain = async (dom) => {
        try {
            await jsonReq(`${API}/projects/${project.project_id}/domains/${dom.domain}`, {
                method: "DELETE",
            });
            toast.success(`${dom.domain} removed`);
            refresh();
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="deploy-dialog"
                className="rounded-none border-foreground max-w-2xl max-h-[90vh] overflow-y-auto loom-scroll p-0"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="tracking-tight text-2xl font-black flex items-center gap-3">
                        <Globe className="w-5 h-5" strokeWidth={1.5} />
                        Deploy
                    </DialogTitle>
                    <DialogDescription>
                        Ship this canvas to a public URL — on a Loom subdomain, your own custom domain, or both.
                    </DialogDescription>
                </DialogHeader>

                {/* Section 1: Loom subdomain */}
                <section className="px-6 py-6 border-b border-border">
                    <div className="overline mb-4">▍ 001 · Loom subdomain</div>
                    {project?.published && project?.slug ? (
                        <div className="space-y-3">
                            <div className="border border-border bg-secondary">
                                <div className="border-b border-border px-3 h-9 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>▍ Public url</span>
                                    <span className="text-accent">● live</span>
                                </div>
                                <div className="p-3 flex items-center gap-2">
                                    <input
                                        readOnly
                                        value={publicUrl}
                                        data-testid="public-url-input"
                                        className="flex-1 bg-transparent font-mono text-xs focus:outline-none"
                                    />
                                    <CopyBadge value={publicUrl} testId="copy-public-url" />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <a
                                    href={publicUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-outline-brutal !h-9 !text-[10px]"
                                    data-testid="visit-public-site"
                                >
                                    Visit <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                </a>
                                <button
                                    data-testid="unpublish-button"
                                    onClick={onUnpublish}
                                    disabled={publishing}
                                    className="btn-outline-brutal !h-9 !text-[10px]"
                                >
                                    {publishing ? "Working…" : "Unpublish"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-4">
                            <div className="font-mono text-xs text-muted-foreground leading-relaxed">
                                ▍ Publish to a short URL like{" "}
                                <span className="text-foreground">/s/a1b2c3d4</span> — free, permanent, updates live on every generation.
                            </div>
                            <button
                                data-testid="confirm-publish"
                                onClick={onPublish}
                                disabled={publishing || !project?.html}
                                className="btn-brutal !h-9 !text-[10px] whitespace-nowrap"
                            >
                                {publishing ? "Publishing…" : "Publish site"}
                                <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </div>
                    )}
                </section>

                {/* Section 1.5: Social share card (OG image) */}
                {project?.published && (
                    <section
                        data-testid="og-image-section"
                        className="px-6 py-6 border-b border-border"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="overline">▍ 001b · Social share card</div>
                            <button
                                data-testid="regenerate-og-image"
                                onClick={regenerateOg}
                                disabled={ogRegenerating || project?.og_generating}
                                className="inline-flex items-center gap-1.5 h-7 px-3 border border-border font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-secondary transition-colors disabled:opacity-40"
                            >
                                {ogRegenerating || project?.og_generating ? (
                                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                ) : (
                                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                                )}
                                {ogRegenerating || project?.og_generating ? "Generating…" : "Regenerate"}
                            </button>
                        </div>

                        {project?.og_image_url ? (
                            <div
                                data-testid="og-image-preview-wrap"
                                className="border border-border bg-secondary"
                            >
                                <div className="border-b border-border px-3 h-9 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>▍ 1200 × 630 · og:image</span>
                                    <span className="text-accent">● live</span>
                                </div>
                                <img
                                    data-testid="og-image-preview"
                                    src={project.og_image_url}
                                    alt="Auto-generated social share card"
                                    className="w-full aspect-[1.91/1] object-cover"
                                    loading="lazy"
                                />
                                <div className="border-t border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                    ▍ Auto-generated by Nano Banana on every publish
                                </div>
                            </div>
                        ) : (
                            <div
                                data-testid="og-image-empty"
                                className="border border-dashed border-border bg-secondary aspect-[1.91/1] grid place-items-center px-6"
                            >
                                {project?.og_generating || ogRegenerating ? (
                                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                        Generating social share card…
                                    </div>
                                ) : (
                                    <div className="text-center space-y-2 max-w-sm">
                                        <ImageLucide
                                            className="w-6 h-6 mx-auto text-muted-foreground"
                                            strokeWidth={1.25}
                                        />
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            No OG image yet — hit Regenerate to create one.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Loom Showcase opt-in */}
                        <label
                            data-testid="showcase-toggle-row"
                            htmlFor="showcase-checkbox"
                            className="mt-4 flex items-start gap-3 cursor-pointer group"
                        >
                            <input
                                id="showcase-checkbox"
                                data-testid="showcase-checkbox"
                                type="checkbox"
                                checked={!!project?.showcase_public}
                                onChange={(e) => toggleShowcase(e.target.checked)}
                                disabled={!project?.og_image_url}
                                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                            />
                            <div className="flex-1">
                                <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-foreground group-hover:text-primary transition-colors">
                                    ▍ Show on Loom Showcase gallery
                                </div>
                                <div className="mt-1 font-mono text-[10px] text-muted-foreground leading-relaxed">
                                    Feature your site on our public gallery — free organic traffic + inspiration for other builders.
                                    {!project?.og_image_url && " (Wait for OG image to generate first.)"}
                                </div>
                            </div>
                        </label>
                    </section>
                )}

                {/* Section 1C: Traffic / pageview analytics */}
                {project?.published && (
                    <section
                        data-testid="analytics-section"
                        className="px-6 py-6 border-b border-border"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="overline">▍ 001c · Traffic</div>
                            <button
                                data-testid="refresh-analytics"
                                onClick={loadAnalytics}
                                disabled={analyticsLoading}
                                className="inline-flex items-center gap-1.5 h-7 px-3 border border-border font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-secondary transition-colors disabled:opacity-40"
                                title="Refresh stats"
                            >
                                {analyticsLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                ) : (
                                    <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
                                )}
                                Refresh
                            </button>
                        </div>

                        {analytics ? (
                            <>
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                    <div className="border border-border p-3">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            ▍ Total
                                        </div>
                                        <div
                                            data-testid="analytics-total"
                                            className="mt-1 text-2xl font-black tracking-tighter tabular-nums"
                                        >
                                            {analytics.total.toLocaleString()}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                            {analytics.unique_total.toLocaleString()} unique
                                        </div>
                                    </div>
                                    <div className="border border-border p-3">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            ▍ Today
                                        </div>
                                        <div
                                            data-testid="analytics-today"
                                            className="mt-1 text-2xl font-black tracking-tighter tabular-nums text-accent"
                                        >
                                            {analytics.today.toLocaleString()}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                            {analytics.today_unique.toLocaleString()} unique
                                        </div>
                                    </div>
                                    <div className="border border-border p-3">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            ▍ Last {analytics.days}d
                                        </div>
                                        <div className="mt-1 text-2xl font-black tracking-tighter tabular-nums">
                                            {analytics.series.reduce((s, d) => s + d.views, 0).toLocaleString()}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                            pageviews
                                        </div>
                                    </div>
                                </div>

                                {/* 7-day sparkline (CSS bars) */}
                                <div
                                    data-testid="analytics-sparkline"
                                    className="border border-border p-3"
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                                        ▍ Daily views · last {analytics.days} days
                                    </div>
                                    <div className="flex items-end gap-1 h-24">
                                        {analytics.series.map((d) => {
                                            const pct = analyticsMaxViews > 0 ? (d.views / analyticsMaxViews) * 100 : 0;
                                            return (
                                                <div
                                                    key={d.day}
                                                    className="flex-1 flex flex-col items-center gap-1 group"
                                                    title={`${d.day}: ${d.views} views (${d.unique} unique)`}
                                                >
                                                    <div className="w-full flex-1 flex items-end">
                                                        <div
                                                            className={`w-full transition-all group-hover:brightness-125 ${
                                                                d.views > 0 ? "bg-primary" : "bg-border"
                                                            }`}
                                                            style={{ height: `${Math.max(pct, 2)}%` }}
                                                        />
                                                    </div>
                                                    <div className="font-mono text-[9px] text-muted-foreground">
                                                        {d.day.slice(5)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Top referrers */}
                                {analytics.top_referrers?.length > 0 && (
                                    <div
                                        data-testid="analytics-referrers"
                                        className="mt-4 border border-border"
                                    >
                                        <div className="border-b border-border px-3 h-9 flex items-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            ▍ Top referrers
                                        </div>
                                        <ul>
                                            {analytics.top_referrers.map((r, i) => (
                                                <li
                                                    key={r.host}
                                                    className={`flex items-center justify-between px-3 py-2 font-mono text-xs ${
                                                        i > 0 ? "border-t border-border" : ""
                                                    }`}
                                                >
                                                    <span className="truncate">{r.host}</span>
                                                    <span className="tabular-nums text-muted-foreground">
                                                        {r.views.toLocaleString()}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : analyticsLoading ? (
                            <div className="border border-border p-6 grid place-items-center">
                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div
                                data-testid="analytics-empty"
                                className="border border-dashed border-border p-6 text-center space-y-2"
                            >
                                <BarChart3
                                    className="w-6 h-6 mx-auto text-muted-foreground"
                                    strokeWidth={1.25}
                                />
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    No traffic data yet — visit your site to record the first pageview.
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Section 2: Custom domains */}
                <section className="px-6 py-6">
                    <div className="overline mb-4">▍ 002 · Custom domain</div>
                    <div className="border border-foreground p-4 flex flex-col sm:flex-row gap-2">
                        <input
                            data-testid="new-domain-input"
                            value={newDomain}
                            onChange={(e) => setNewDomain(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addDomain()}
                            placeholder="yourdomain.com"
                            className="flex-1 bg-transparent font-mono text-sm p-2 focus:outline-none placeholder:text-muted-foreground border border-transparent focus:border-border"
                        />
                        <button
                            data-testid="attach-domain"
                            disabled={adding || !newDomain.trim()}
                            onClick={addDomain}
                            className="btn-brutal !h-10 !text-[10px] whitespace-nowrap"
                        >
                            {adding ? "Attaching…" : "Attach domain"}
                        </button>
                    </div>

                    {loading ? (
                        <div className="mt-4 overline">▍ Loading domains…</div>
                    ) : domains.length === 0 ? null : (
                        <ul className="mt-4 space-y-4">
                            {domains.map((d) => (
                                <li
                                    key={d.domain}
                                    data-testid={`domain-row-${d.domain}`}
                                    className="border border-border"
                                >
                                    <div className="flex items-center justify-between px-4 h-11 border-b border-border">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="font-black tracking-tight truncate">{d.domain}</div>
                                            {d.verified ? (
                                                <span
                                                    data-testid={`domain-verified-${d.domain}`}
                                                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-accent"
                                                >
                                                    <span className="w-1.5 h-1.5 bg-accent" /> live
                                                </span>
                                            ) : (
                                                <span
                                                    data-testid={`domain-pending-${d.domain}`}
                                                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground"
                                                >
                                                    <span className="w-1.5 h-1.5 border border-muted-foreground" /> pending DNS
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {!d.verified && (
                                                <button
                                                    data-testid={`verify-domain-${d.domain}`}
                                                    onClick={() => verify(d)}
                                                    disabled={verifying === d.domain}
                                                    className="inline-flex items-center gap-1.5 h-7 px-3 border border-foreground bg-foreground text-background font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-primary hover:border-primary transition-colors disabled:opacity-40"
                                                >
                                                    {verifying === d.domain ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                                    ) : (
                                                        <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
                                                    )}
                                                    Check DNS
                                                </button>
                                            )}
                                            <button
                                                data-testid={`delete-domain-${d.domain}`}
                                                onClick={() => removeDomain(d)}
                                                className="w-7 h-7 grid place-items-center border border-border text-muted-foreground hover:text-accent hover:border-foreground transition-colors"
                                                title="Remove"
                                            >
                                                <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                    {!d.verified && (
                                        <div className="px-4 py-4 bg-secondary space-y-3">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                                ▍ At your DNS provider, add these two records:
                                            </div>
                                            <div className="grid grid-cols-[54px_1fr_1fr_auto] gap-2 items-center text-xs font-mono">
                                                <div className="text-muted-foreground uppercase text-[10px] tracking-[0.16em]">CNAME</div>
                                                <div className="truncate" title={d.domain}>{d.domain}</div>
                                                <div className="truncate" title={cnameTarget}>{cnameTarget}</div>
                                                <CopyBadge value={cnameTarget} testId={`copy-cname-${d.domain}`} />

                                                <div className="text-muted-foreground uppercase text-[10px] tracking-[0.16em]">TXT</div>
                                                <div className="truncate" title={`_loom-verify.${d.domain}`}>_loom-verify.{d.domain}</div>
                                                <div className="truncate" title={d.verification_token}>{d.verification_token}</div>
                                                <CopyBadge value={d.verification_token} testId={`copy-txt-${d.domain}`} />
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                                DNS can take 1-5 minutes to propagate. Hit <span className="text-foreground">Check DNS</span> when ready.
                                            </div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <DialogFooter className="px-6 py-4 border-t border-border bg-secondary">
                    <div className="w-full font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        ▍ Deployments update automatically on every generation
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
